<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Products</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Products List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="products"
      :fetch="fetchProducts"
    >
      <template #table-toolbar>
        <button type="button" class="shared-table-toolbar-button" @click="showModal = true">
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Product
        </button>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-center">
            <button
              class="mr-2 shared-table-cells-button round edit"
              @click="() => handleSetEditableProduct(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              class="shared-table-cells-button round delete"
              @click="handleDeleteProduct(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Modals -->
    <ModalPlus
      :title="modalTitle"
      :buttonText="editable ? 'Save' : 'Add'"
      :show="showModal"
      :onClose="handleClose"
      :onProceed="handleSubmit(handleProceed)"
      :disabled="tab.order === 1"
      maxWidth="1200px"
    >
      <div class="flex flex-col pt-8" :style="{ height: '1200px' }">
        <Tabs v-if="editable" :tabs="tabs" @change="t => (tab = t)" />
        <div class="flex-1 overflow-y-hidden">
          <ProductForm
            v-if="tab.order === 0 || !editable"
            :series="series"
            :accessories="accessories"
            :initialValues="editableProduct"
            :machineTypes="machineTypes"
            :errors="errors"
            @set="handleSetField"
          />
          <ProductGroupForm v-else-if="tab.order === 1" :series="series" />
          <ProductVideoForm
            v-else-if="tab.order === 2 && editable"
            :videos="editableProduct.videos.data"
            :product_id="editableProduct.id"
            :language="language"
          />
          <ProductResourceForm
            v-else-if="tab.order === 3 && editable"
            :resources="editableProduct.productResources.data"
            :product_id="editableProduct.id"
            :language="language"
          />
        </div>
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { safeNullDataset, setTranslationColumn, typeCheck } from '@/utils';

import { formMixin } from '@/mixins';

import VSvgIcon from '@/components/base/VSvgIcon';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import Tabs from '@/components/shared/Tabs';
import ProductForm from '@/components/admin/ProductForm';
import ProductGroupForm from '@/components/admin/ProductGroupForm';
import ProductVideoForm from '@/components/admin/ProductVideoForm';
import ProductResourceForm from '@/components/admin/ProductResourceForm';
import Table from '@/components/shared/table/Table';

export default {
  name: 'AdminProducts',
  components: {
    VSvgIcon,
    ModalPlus,
    ProductForm,
    ProductGroupForm,
    ProductVideoForm,
    ProductResourceForm,
    Tabs,
    Table,
  },
  mixins: [
    formMixin(
      {
        netsuite_name: '',
        display_name: '',
        zendesk_value: '',
        netsuite_id: '',
        description: '',
        product_group: '',
        thumbnail: null,
        parts_book: null,
        quick_start_guide: null,
        accessories: [],
        machine_type: null,
        count_towards_threshold: 1,
        has_reward: 1,
        external_shopify_link: '',
      },
      [
        'thumbnail',
        'parts_book',
        'quick_start_guide',
        'accessories',
        'machine_type',
        'display_name',
        'zendesk_value',
        'count_towards_threshold',
        'has_reward',
        'external_shopify_link',
      ],
    ),
  ],
  data() {
    return {
      showModal: false,
      editableProduct: {},
      tab: {},
      accessories: [],
    };
  },
  computed: mapState({
    products: state => ({
      data: safeNullDataset(state)(['admin', 'products', 'data']).map(p => ({
        "it's translation": setTranslationColumn(p),
        'netsuite name': p.netsuite_name,
        'display name': p.display_name,
        'netsuite id': p.netsuite_id,
        'group name': p.group ? p.group.name : '-',
        group_name: p.group ? p.group.name : null,
        ...p,
      })),
      meta: state.admin.products.meta,
    }),
    language: state => state.language,
    series: state => state.admin.series,
    machineTypes: state => state.admin.machineTypes.data,
    seriesMappedByName: state =>
      state.admin.series.reduce((acc, serie) => ((acc[serie.name] = serie), acc), {}),
    editable() {
      return this.editableProduct && !!Object.keys(this.editableProduct).length;
    },
    tabs() {
      return [
        { name: 'Product', order: 0 },
        { name: 'Product Groups', order: 1 },
        { name: 'Product Videos', order: 2 },
        { name: 'Product Resources', order: 3 },
      ];
    },
    modalTitle() {
      return this.tab.order === 0
        ? this.editable
          ? 'Edit Product'
          : 'Add Product'
        : 'Add Product Group';
    },
  }),
  methods: {
    async fetchProducts(params) {
      return await this.$store.dispatch('admin/getProducts', params);
    },
    handleSetEditableProduct(product) {
      console.log(product);
      this.editableProduct = product;
      this.showModal = true;
    },
    handleClose() {
      this.handleResetFields();
      this.handleResetErrors();
      this.editableProduct = {};
      this.showModal = false;
    },
    handleProceed(data) {
      this.editable
        ? this.handleUpdateProduct({ id: this.editableProduct.id, data })
        : this.handleAddProduct(data);
    },
    setFormData(data, exclude = []) {
      const reducer = (acc, [key, value]) => {
        // check against this array of keys on the final check to only append unchecked props to form data
        const excludedFromFinalCheck = [
          ...exclude,
          'product_group',
          'parts_book',
          'quick_start_guide',
          'accessories',
        ];

        if (key === 'product_group' && value)
          return acc.append('group_id', this.seriesMappedByName[value].id), acc;
        if (key === 'parts_book' && value) return acc.append('resources[1]', value), acc;
        if (key === 'quick_start_guide' && value) return acc.append('resources[2]', value), acc;
        if (key === 'accessories' && value.length)
          return (
            value.forEach((accessory, i) => acc.append(`accessories[${i}]`, accessory.id)), acc
          );
        if ((typeCheck(value).isNum || value) && !excludedFromFinalCheck.includes(key))
          return acc.append(key, value), acc;
        return acc;
      };

      return Object.entries(data).reduce(reducer, new FormData());
    },
    async handleAddProduct({ machine_type, ...rest }) {
      const { code: lang_code } = this.language;
      const data = machine_type
        ? { ...rest, machine_type_id: machine_type.id, lang_code }
        : { ...rest, lang_code };
      const formData = this.setFormData(data);
      await this.$store.dispatch('admin/addProduct', { data: formData });
      this.handleClose();
    },
    async handleUpdateProduct({ id, data: { machine_type, ...rest } }) {
      const { code: lang_code } = this.language;
      const d = machine_type
        ? { ...rest, machine_type_id: machine_type.id, lang_code }
        : { ...rest, lang_code };
      const formData = this.setFormData(d);
      await this.$store.dispatch('admin/updateProduct', { id, data: formData });
      this.handleClose();
    },
    async handleDeleteProduct(id) {
      await this.$store.dispatch('admin/deleteProduct', id);
    },
  },
  async mounted() {
    let series = this.$store.state.admin.series;
    if (!series.length) {
      series = await this.$store.dispatch('admin/getSeries');
    }

    let machineTypes = this.$store.state.admin.machineTypes;
    if (!Object.keys(machineTypes).length) {
      machineTypes = await this.$store.dispatch('admin/getMachineTypes');
    }

    let groupCategories = this.$store.state.admin.groupCategories;
    if (!Object.keys(groupCategories).length) {
      groupCategories = await this.$store.dispatch('admin/getGroupCategories');
    }

    const { data } = await this.$http.get('/admin/accessories-all');
    this.accessories = data;
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: "it's translation", isSort: false, classes: { th: 'w-32', td: 'w-32' } },
      { column: 'netsuite name', isSort: true, orderBy: 'netsuite_name' },
      { column: 'display name', isSort: true, orderBy: 'display_name' },
      { column: 'netsuite id', isSort: true, orderBy: 'netsuite_id' },
      { column: 'group name', isSort: false },
      { column: 'actions', isSort: false, classes: { th: 'w-32', td: 'w-32' } },
    ];
  },
};
</script>
